/**
 * @copyright 2022 Nuance Communications Inc.
 * All Rights Reserved.
 */
import * as React from "react";
import { Checkbox, FocusZone, Label, Stack } from "@fluentui/react";
import { Levels } from "@nuance/hierarchical-settings-ui-domain";
import {
    AccessLevelSelectionCommands,
    IAccessLevelSelectionState
} from "./AccessLevelSelectionViewModel";
import { skipInitialRenderEffect } from "../../CustomHooks/SkipInitialRenderEffect";

const stackTokens = { childrenGap: 5 };

/**
 * Properties of the AccessLevelSelection component.
 */
interface IAccessLevelSelectionProps {
    accessLevel: Levels;
    onAccessLevelChange?: (newAccessLevel: Levels) => void;
}

const accessLevelSelectionCommands = new AccessLevelSelectionCommands();
const cmds = accessLevelSelectionCommands.Cmds;

/**
 * AccessLevelSelection component.
 *
 * @param props AccessLevelSelection input properties
 * @returns
 */
const AccessLevelSelection = (props: IAccessLevelSelectionProps): JSX.Element => {
    const initialState: IAccessLevelSelectionState = {
        systemChecked: (props.accessLevel & Levels.System) === Levels.System,
        orgChecked: (props.accessLevel & Levels.Org) === Levels.Org,
        siteChecked: (props.accessLevel & Levels.Site) === Levels.Site,
        groupChecked: (props.accessLevel & Levels.Group) === Levels.Group,
        userChecked: (props.accessLevel & Levels.User) === Levels.User
    };

    const [state, dispatch] = React.useReducer(accessLevelSelectionCommands.Reducer, initialState);

    const postChanges = () => {
        if (props.onAccessLevelChange !== undefined) {
            const level = accessLevelSelectionCommands.CalculateAccessLevel(state);
            props.onAccessLevelChange(level);
        }
    };

    skipInitialRenderEffect(postChanges, [state]);

    const onSystemChange = () => {
        dispatch(cmds.toggleSystem());
    };

    const onOrgChange = () => {
        dispatch(cmds.toggleOrg());
    };

    const onSiteChange = () => {
        dispatch(cmds.toggleSite());
    };

    const onGroupChange = () => {
        dispatch(cmds.toggleGroup());
    };

    const onUserChange = () => {
        dispatch(cmds.toggleUser());
    };

    return (
        <Stack>
            <FocusZone>
                <Label>Access Level</Label>

                <Stack tokens={stackTokens}>
                    <Checkbox
                        checked={state.systemChecked}
                        onChange={onSystemChange}
                        label="System"
                    />
                    <Checkbox
                        checked={state.orgChecked}
                        onChange={onOrgChange}
                        label="Organization"
                    />
                    <Checkbox checked={state.siteChecked} onChange={onSiteChange} label="Site" />
                    <Checkbox checked={state.groupChecked} onChange={onGroupChange} label="Group" />
                    <Checkbox checked={state.userChecked} onChange={onUserChange} label="User" />
                </Stack>
            </FocusZone>
        </Stack>
    );
};

export default AccessLevelSelection;
